import {useCallback, useEffect} from 'react'
import {useGlobalSearch} from '../store/hooks/globalSearch'
import {useLocalStorage} from './useLocalStorage'
import {RECENT_SEARCHES_KEY} from '../components/Dashboard/helper'

const MAX_RECENT_SEARCHES = 5

/**
 * A custom hook for managing recent searches with localStorage persistence
 * @returns {Object} Methods and state for managing recent searches
 */
export const useRecentSearches = () => {
  const {globalSearchFilterOptions} = useGlobalSearch()
  const [recentSearches, setRecentSearches] = useLocalStorage(RECENT_SEARCHES_KEY, [])

  /**
   * Add a search term to recent searches
   * If it already exists, it will be moved to the top (most recent)
   * @param {string} searchTerm - The search term to add
   */
  const addRecentSearch = useCallback(
    (searchTerm) => {
      if (!searchTerm || searchTerm.length < 3) return

      setRecentSearches((prevSearches) => {
        const filteredSearches = prevSearches.filter((term) => term !== searchTerm)
        const updatedSearches = [searchTerm, ...filteredSearches]
        return updatedSearches.slice(0, MAX_RECENT_SEARCHES)
      })
    },
    [setRecentSearches]
  )

  /**
   * Remove a specific search term from recent searches
   * @param {string} searchTerm - The search term to remove
   */
  const removeRecentSearch = useCallback(
    (searchTerm) => {
      setRecentSearches((prevSearches) => prevSearches.filter((term) => term !== searchTerm))
    },
    [setRecentSearches]
  )

  /**
   * Remove the filter from recent searches if it doesn't exist in the global search filter options
   */

  const updateRecentSearches = (filter) => {
    setRecentSearches((prevSearches) => prevSearches.filter((term) => term !== filter))
  }

  const handleRecentSearchesUpdate = (globalSearchFilters) => {
    for (const filter of recentSearches || []) {
      if (!globalSearchFilters.includes(filter)) {
        updateRecentSearches(filter)
      }
    }
  }

  /**
   * Ensures that recent searches are reflected in the global search filter options, if not remove it from recent searches
   */
  useEffect(() => {
    if (globalSearchFilterOptions?.length > 0) {
      const globalSearchFilters = globalSearchFilterOptions?.map((obj) => obj.label)
      handleRecentSearchesUpdate(globalSearchFilters)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalSearchFilterOptions])

  return {
    recentSearches,
    addRecentSearch,
    removeRecentSearch
  }
}
